import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtMoney } from "@/lib/apiClient";
import { devLog } from "@/lib/devLog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

/**
 * BOQSectionsSummary — extracted from RABilling.jsx (iter46 refactor).
 *
 * Renders the Sub-heads tab: a per-section (sub-head) rollup table with
 * Contract / Billed / Delivered / Measured columns + a progress bar per
 * section. Grand totals in the header. Falls back to client-side aggregation
 * from `boqItems` if the API hasn't returned yet (or the BOQ isn't saved).
 *
 * Props:
 *   • boqId    — id of the BOQ (null when creating a fresh BOQ that isn't saved yet)
 *   • boqItems — the items[] array to fall back on for client-side rollup
 */
export default function BOQSectionsSummary({ boqId, boqItems }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!boqId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/boqs/${boqId}/sections-summary`);
      setSummary(data);
    } catch (e) { devLog.warn(e); }
    finally { setLoading(false); }
  }, [boqId]);
  useEffect(() => { load(); }, [load]);

  const localSummary = useMemo(() => {
    if (summary) return summary;
    const sections = {};
    const order = [];
    let grandContract = 0;
    (boqItems || []).forEach((it) => {
      const sec = (it.section || "").trim() || "— Ungrouped —";
      if (!sections[sec]) {
        sections[sec] = {
          section: sec, item_count: 0, contract_value: 0, billed_value: 0,
          pct_billed: 0, delivered_value: 0, measured_value: 0,
          has_client_section_label: !!(it.section || "").trim(),
        };
        order.push(sec);
      }
      const val = (Number(it.contract_qty) || 0) * (Number(it.rate) || 0);
      sections[sec].item_count += 1;
      sections[sec].contract_value += val;
      grandContract += val;
    });
    return {
      sections: order.map((s) => sections[s]),
      grand_contract_value: grandContract,
      grand_billed_value: 0,
      grand_pct_billed: 0,
    };
  }, [summary, boqItems]);

  if (!boqId) {
    return <div className="text-[12.5px] text-slate-400">Save the BOQ first to see live sub-head roll-ups (billed % against contract).</div>;
  }
  if (loading && !summary) {
    return <div className="text-[12.5px] text-slate-500 py-4">Loading sub-head summary…</div>;
  }

  const secs = (localSummary?.sections) || [];
  const clientLabelCount = secs.filter((s) => s.has_client_section_label).length;

  return (
    <div className="space-y-3" data-testid="boq-sections-summary">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">
          Sub-heads · {secs.length}
          {clientLabelCount > 0 && (
            <span className="text-slate-500 font-normal normal-case ml-1">
              · {clientLabelCount} from client format
            </span>
          )}
        </div>
        <div className="text-[11.5px] flex items-center gap-4 font-mono-num">
          <div>Grand Contract: <span className="font-bold">{fmtMoney(localSummary?.grand_contract_value || 0)}</span></div>
          <div>Grand Billed: <span className="font-bold text-emerald-700">{fmtMoney(localSummary?.grand_billed_value || 0)}</span></div>
          <div>Overall %: <span className="font-bold text-[#EA580C]">{(localSummary?.grand_pct_billed || 0).toFixed(1)}%</span></div>
        </div>
      </div>
      <div className="border border-slate-200 rounded-sm overflow-hidden">
        <Table className="bc-list">
          <TableHeader>
            <TableRow>
              <TableHead className="w-64">Sub-head</TableHead>
              <TableHead className="w-16 text-right">Items</TableHead>
              <TableHead className="w-32 text-right">Contract</TableHead>
              <TableHead className="w-32 text-right">Billed</TableHead>
              <TableHead className="w-32 text-right">Delivered</TableHead>
              <TableHead className="w-32 text-right">Measured</TableHead>
              <TableHead className="w-24 text-right">%</TableHead>
              <TableHead className="w-40">Progress</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {secs.map((s, i) => (
              <TableRow key={s.section || `sec-${i}`} data-testid={`section-row-${i}`}>
                <TableCell>
                  <div className="font-semibold text-slate-800">{s.section}</div>
                  {s.has_client_section_label && <div className="text-[9.5px] uppercase tracking-widest text-[#EA580C] mt-0.5">Client sub-head</div>}
                </TableCell>
                <TableCell className="text-right font-mono-num">{s.item_count}</TableCell>
                <TableCell className="text-right font-mono-num">{fmtMoney(s.contract_value || 0)}</TableCell>
                <TableCell className="text-right font-mono-num text-emerald-700 font-semibold">{fmtMoney(s.billed_value || 0)}</TableCell>
                <TableCell className="text-right font-mono-num text-slate-600">{fmtMoney(s.delivered_value || 0)}</TableCell>
                <TableCell className="text-right font-mono-num text-slate-600">{fmtMoney(s.measured_value || 0)}</TableCell>
                <TableCell className="text-right font-mono-num font-bold text-[#EA580C]">{Number(s.pct_billed || 0).toFixed(1)}%</TableCell>
                <TableCell>
                  <div className="w-full h-2 rounded-sm bg-slate-200 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#EA580C] to-[#c2410c]"
                      style={{ width: `${Math.min(100, Number(s.pct_billed || 0))}%` }}
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {secs.length === 0 && <div className="p-6 text-center text-slate-400 text-[12px]">No items yet. Add items in the Items tab first.</div>}
      </div>
      <div className="text-[10.5px] text-slate-500 italic">
        Tip: Sub-head groupings come from the "Section" field on each item (auto-parsed from the client's Excel sub-heads). Edit an item's section in the Items tab to move it to a different sub-head.
      </div>
    </div>
  );
}
